import { createFileRoute, redirect, useRouter } from '@tanstack/react-router'
import { ShieldCheck } from 'lucide-react'
import { TwoFactorEnrollment } from '@/features/security/two-factor-enrollment'
import { markTwoFactorEnabled } from '@/features/security/api'
import { ensureSession } from '@/lib/api'
import { needsTwoFactorEnrollment, useAuthStore } from '@/lib/auth'
import { notifySuccess } from '@/lib/notify'
import { ROUTES } from '@/lib/routes'
import { endSession } from '@/lib/session'

/**
 * Activación obligatoria de la verificación en dos pasos: la política de la
 * institución la exige al rol del usuario y, hasta activarla, no puede usar
 * la aplicación (el API también rechaza sus peticiones).
 */
export const Route = createFileRoute('/_auth/two-factor-setup')({
  beforeLoad: async ({ location }) => {
    if (!(await ensureSession())) throw redirect({ to: ROUTES.login, search: { redirect: location.href } })
    const employee = useAuthStore.getState().employee
    if (employee?.mustChangePassword) throw redirect({ to: ROUTES.changePassword })
    if (!needsTwoFactorEnrollment(employee)) throw redirect({ to: ROUTES.dashboard })
  },
  component: ForcedTwoFactorSetup,
})

function ForcedTwoFactorSetup() {
  const router = useRouter()
  const employee = useAuthStore((s) => s.employee)

  const leave = async () => {
    await endSession()
    await router.navigate({ to: ROUTES.login })
  }

  return (
    <div className="rounded-2xl border border-border bg-card p-6 shadow-[0_8px_40px_hsl(var(--shadow-color)/0.08)] sm:p-8">
      <div className="mb-6 flex items-start gap-3">
        <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <ShieldCheck className="size-5" aria-hidden />
        </span>
        <div>
          <h2 className="text-lg font-bold">Active la verificación en dos pasos</h2>
          <p className="text-sm text-muted-foreground">
            {employee?.fullName ? `${employee.fullName}, su` : 'Su'} institución exige este paso para su rol antes de continuar.
          </p>
        </div>
      </div>
      <TwoFactorEnrollment
        account={employee?.email ?? ''}
        onEnabled={() => markTwoFactorEnabled(true)}
        onCompleted={() => {
          notifySuccess('Verificación en dos pasos activada', 'La próxima vez se le pedirá el código al entrar.')
          void router.navigate({ to: ROUTES.dashboard })
        }}
      />
      <p className="mt-6 text-center text-sm">
        <button type="button" onClick={() => void leave()} className="text-muted-foreground hover:text-foreground hover:underline">
          Cerrar sesión
        </button>
      </p>
    </div>
  )
}
